import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { Funcionario } from '@/context/FuncionariosContext';
import { useFuncionarios } from '@/context/FuncionariosContext';
import { calcularProgressao, temDezoitoAnosEm30Ago2023 } from '@/utils/progressaoUtils';
import { useAppSettings } from '@/context/AppSettingsContext';
import { supabase } from '@/lib/supabase';
import { useModalAnimation } from '@/hooks/useModalAnimation';
import { useModalOverflow } from '@/hooks/useModalOverflow';

interface RegistarProgressaoModalProps {
  funcionario: Funcionario;
  pontosCalculados: number;
  limiarEfetivo?: number;
  jaUsouAcelerador?: boolean;
  onClose: () => void;
  onSuccess: (nome: string) => void;
}

type TabId = 'automatica' | 'manual';

export default function RegistarProgressaoModal({
  funcionario,
  pontosCalculados,
  limiarEfetivo,
  jaUsouAcelerador = false,
  onClose,
  onSuccess,
}: RegistarProgressaoModalProps) {
  const { updateFuncionario } = useFuncionarios();
  const { settings } = useAppSettings();
  const { animating, handleClose } = useModalAnimation(onClose);
  useModalOverflow(true);

  const [activeTab, setActiveTab] = useState<TabId>('automatica');

  // ── Tab Automática ──
  const limiarUsado = limiarEfetivo ?? settings.pontos_progressao;
  const [aceleradorRemuneratorio, setAceleradorRemuneratorio] = useState(false);
  // Quando o acelerador está ativo, o limiar de desconto é 6 pts
  const limiarDesconto = aceleradorRemuneratorio ? 6 : limiarUsado;

  const prog = calcularProgressao(pontosCalculados, funcionario.nivelAtual, limiarDesconto);
  const isRegraEspecial = limiarEfetivo !== undefined && limiarEfetivo !== settings.pontos_progressao;
  const qualificaRegra18 = isRegraEspecial && temDezoitoAnosEm30Ago2023(funcionario.dataEntradaFuncaoPublica);
  const pontosAposProgressao = Math.max(0, pontosCalculados - limiarDesconto);

  const [dataProgressao, setDataProgressao] = useState(new Date().toISOString().split('T')[0]);
  const [novoNivel, setNovoNivel] = useState(prog.proximoNivel);
  const [novoVencimento, setNovoVencimento] = useState(funcionario.vencimento ?? 0);
  const [observacoes, setObservacoes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // ── Tab Manual ──
  const limiarNormalManual = settings.pontos_progressao ?? 8;
  const [pmPontosDescontar, setPmPontosDescontar] = useState<string>(String(limiarNormalManual));
  const [pmNovoNivel, setPmNovoNivel] = useState<number>(funcionario.nivelAtual + 1);
  const [pmData, setPmData] = useState(new Date().toISOString().split('T')[0]);
  const [pmNovoVencimento, setPmNovoVencimento] = useState<number>(funcionario.vencimento ?? 0);
  const [pmObservacoes, setPmObservacoes] = useState('');
  const [pmAceleradorRemuneratorio, setPmAceleradorRemuneratorio] = useState(false);
  const pmPontosAntesAcelerador = useRef<string>(String(limiarNormalManual));
  const [pmSaving, setPmSaving] = useState(false);
  const [pmError, setPmError] = useState('');

  // Ajusta pontos da tab manual quando o acelerador muda
  useEffect(() => {
    if (pmAceleradorRemuneratorio) {
      pmPontosAntesAcelerador.current = pmPontosDescontar;
      setPmPontosDescontar('6');
    } else {
      // Se o valor atual for 6, restaura o valor anterior; senão mantém
      if (parseFloat(pmPontosDescontar) === 6) {
        setPmPontosDescontar(pmPontosAntesAcelerador.current || String(limiarNormalManual));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pmAceleradorRemuneratorio]);

  const pmPontosNum = Math.max(0, parseFloat(pmPontosDescontar) || 0);
  const pmPontosApos = Math.max(0, pontosCalculados - pmPontosNum);

  const initials = funcionario.nome
    .split(' ')
    .filter((_, i, arr) => i === 0 || i === arr.length - 1)
    .map((n) => n[0])
    .join('')
    .toUpperCase();

  // Atualiza novo nível quando o limiar de desconto muda (acelerador pode afetar)
  useEffect(() => {
    setNovoNivel(prog.proximoNivel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aceleradorRemuneratorio, limiarUsado]);

  // ── Handlers ──
  async function handleConfirmarAutomatica() {
    if (!dataProgressao) { setError('Data de progressão obrigatória.'); return; }
    setSaving(true); setError('');
    try {
      const novosDescontados = (funcionario.pontosDescontados ?? 0) + limiarDesconto;
      const progressaoId = 'prog_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7);

      const { error: progErr } = await supabase.from('progressoes').insert({
        id: progressaoId,
        funcionario_id: funcionario.id,
        data_progressao: dataProgressao,
        nivel_anterior: funcionario.nivelAtual,
        nivel_novo: novoNivel,
        pontos_descontados: limiarDesconto,
        observacoes: observacoes || null,
        acelerador_remuneratorio: aceleradorRemuneratorio,
      });
      if (progErr) throw new Error(progErr.message);

      const updated: Funcionario = {
        ...funcionario,
        nivelAtual: novoNivel,
        novoNivel2025: novoNivel,
        ultimaProgressao: dataProgressao,
        vencimento: novoVencimento,
        pontosDescontados: novosDescontados,
        descricaoSubida: observacoes || `Progressão registada em ${dataProgressao}`,
      };

      await updateFuncionario(updated, funcionario.id);
      onSuccess(funcionario.nome);
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erro ao registar progressão.');
    } finally {
      setSaving(false);
    }
  }

  async function handleConfirmarManual() {
    if (!pmData) { setPmError('Data obrigatória.'); return; }
    if (pmPontosNum <= 0) { setPmError('Pontos a descontar devem ser maiores que zero.'); return; }
    if (pmNovoNivel <= funcionario.nivelAtual) { setPmError('O novo nível deve ser superior ao atual.'); return; }

    setPmSaving(true); setPmError('');
    try {
      const novosDescontadosPm = (funcionario.pontosDescontados ?? 0) + pmPontosNum;
      const progressaoId = 'prog_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7);

      const { error: progErr } = await supabase.from('progressoes').insert({
        id: progressaoId,
        funcionario_id: funcionario.id,
        data_progressao: pmData,
        nivel_anterior: funcionario.nivelAtual,
        nivel_novo: pmNovoNivel,
        pontos_descontados: pmPontosNum,
        observacoes: pmObservacoes || 'Progressão manual',
        acelerador_remuneratorio: pmAceleradorRemuneratorio,
      });
      if (progErr) throw new Error(progErr.message);

      const updated: Funcionario = {
        ...funcionario,
        nivelAtual: pmNovoNivel,
        novoNivel2025: pmNovoNivel,
        ultimaProgressao: pmData,
        vencimento: pmNovoVencimento > 0 ? pmNovoVencimento : funcionario.vencimento,
        pontosDescontados: novosDescontadosPm,
        descricaoSubida: pmObservacoes || `Progressão manual registada em ${pmData}`,
      };

      await updateFuncionario(updated, funcionario.id);

      await supabase.from('historico_alteracoes').insert({
        id: 'ha_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7),
        funcionario_id: funcionario.id,
        funcionario_nome: funcionario.nome,
        tipo_acao: 'progressao',
        campo_alterado: 'nivel_atual',
        valor_anterior: String(funcionario.nivelAtual),
        valor_novo: String(pmNovoNivel),
        descricao: `Progressão manual: nível ${funcionario.nivelAtual} → ${pmNovoNivel}. Pontos descontados: ${pmPontosNum}. ${pmObservacoes}`,
        data_alteracao: new Date().toISOString(),
      });

      onSuccess(funcionario.nome);
      onClose();
    } catch (e: unknown) {
      setPmError(e instanceof Error ? e.message : 'Erro ao registar progressão.');
    } finally {
      setPmSaving(false);
    }
  }

  const tabs: { id: TabId; label: string; icon: string; desc: string }[] = [
    { id: 'automatica', label: 'Por Pontos',      icon: 'ri-bar-chart-line',      desc: 'Baseada no limiar SIADAP' },
    { id: 'manual',     label: 'Manual',           icon: 'ri-edit-circle-line',    desc: 'Livre, sem limiar' },
  ];

  const modal = (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={handleClose}>
      <div className={`absolute inset-0 bg-black/50 backdrop-blur-sm ${animating ? 'modal-backdrop-active' : 'modal-backdrop-enter'}`} />
      <div
        className={`relative bg-white rounded-2xl w-full max-w-5xl overflow-hidden border border-zinc-100 flex flex-col max-h-[96vh] ${animating ? 'modal-panel-active' : 'modal-panel-enter'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-zinc-100 flex items-center justify-between flex-shrink-0 bg-zinc-50/60">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-100 flex-shrink-0">
              <i className="ri-arrow-up-circle-line text-emerald-600 text-lg" />
            </div>
            <div>
              <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-0.5">Registar</p>
              <h2 className="text-[15px] font-bold text-zinc-900 leading-tight">Progressão</h2>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Funcionário strip */}
        <div className="px-6 py-3.5 border-b border-zinc-100 flex items-center gap-3 bg-white flex-shrink-0">
          <div className="w-9 h-9 flex items-center justify-center rounded-full bg-zinc-900 text-white text-[12px] font-bold flex-shrink-0">
            {initials}
          </div>
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-zinc-800 truncate">{funcionario.nome}</p>
            <p className="text-[11px] text-zinc-400">{funcionario.carreira} · <span className="font-mono">#{funcionario.id}</span> · Nível {funcionario.nivelAtual}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 border-b border-zinc-100 flex items-center gap-0 bg-white flex-shrink-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`whitespace-nowrap flex items-center gap-2 px-4 py-3.5 text-[13px] font-medium border-b-2 transition-colors cursor-pointer ${
                activeTab === tab.id
                  ? 'border-zinc-900 text-zinc-900'
                  : 'border-transparent text-zinc-400 hover:text-zinc-600'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${tab.icon} text-sm`} />
              </div>
              {tab.label}
            </button>
          ))}
        </div>

        {/* ══════════════════════════════════════════
            TAB: POR PONTOS (automática)
        ══════════════════════════════════════════ */}
        {activeTab === 'automatica' && (
          <>
            <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">

              {/* Transição de nível */}
              <div>
                <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-3">Transição de Nível</p>
                <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
                  <div className="flex items-center justify-center gap-6 mb-4">
                    <div className="text-center">
                      <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-white border border-emerald-200 mx-auto mb-1.5">
                        <span className="text-[22px] font-bold text-zinc-700">{funcionario.nivelAtual}</span>
                      </div>
                      <p className="text-[11px] text-zinc-500">Nível atual</p>
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-emerald-500 text-white">
                        <i className="ri-arrow-right-line text-sm" />
                      </div>
                      <span className="text-[10px] text-emerald-600 font-semibold">+{novoNivel - funcionario.nivelAtual}</span>
                    </div>
                    <div className="text-center">
                      <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-emerald-500 text-white mx-auto mb-1.5">
                        <span className="text-[22px] font-bold">{novoNivel}</span>
                      </div>
                      <p className="text-[11px] text-emerald-700 font-medium">Novo nível</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 pt-3 border-t border-emerald-200/60">
                    <div className="text-center">
                      <p className="text-[11px] text-zinc-500 mb-0.5">Pontos atuais</p>
                      <p className="text-[14px] font-bold text-zinc-700">{pontosCalculados}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[11px] text-zinc-500 mb-0.5">Desconto</p>
                      <p className="text-[14px] font-bold text-rose-600">−{limiarDesconto}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[11px] text-zinc-500 mb-0.5">Ficam</p>
                      <p className="text-[14px] font-bold text-emerald-700">{pontosAposProgressao}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Regra especial 18 anos */}
              {qualificaRegra18 && (
                <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg text-amber-600 flex-shrink-0">
                    <i className="ri-award-fill text-sm" />
                  </div>
                  <div>
                    <p className="text-[12px] font-bold text-amber-800 mb-0.5">Regra especial 2024 — 18 anos de serviço</p>
                    <p className="text-[11px] text-amber-700 leading-relaxed">
                      18+ anos a 30/08/2023 — limiar reduzido para <strong>{limiarDesconto} pts</strong>.
                    </p>
                  </div>
                </div>
              )}

              {/* Aviso Acelerador já utilizado */}
              {jaUsouAcelerador && (
                <div className="flex items-start gap-3 bg-zinc-50 border border-zinc-200 rounded-xl p-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded-lg text-zinc-400 flex-shrink-0">
                    <i className="ri-flashlight-fill text-sm" />
                  </div>
                  <div>
                    <p className="text-[12px] font-bold text-zinc-600 mb-0.5">Acelerador Remuneratório já utilizado</p>
                    <p className="text-[11px] text-zinc-500 leading-relaxed">
                      Este funcionário já usufruiu do Acelerador numa progressão anterior. Só pode ser usado uma vez.
                    </p>
                  </div>
                </div>
              )}

              {/* Campos */}
              <div>
                <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-3">Dados da Progressão</p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-1.5">Data da Progressão *</label>
                    <input type="date" value={dataProgressao} onChange={(e) => setDataProgressao(e.target.value)}
                      className="w-full px-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300" />
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-1.5">Novo Nível</label>
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => setNovoNivel((v) => Math.max(funcionario.nivelAtual + 1, v - 1))}
                        className="whitespace-nowrap w-9 h-9 flex items-center justify-center rounded-lg border border-zinc-200 text-zinc-600 hover:bg-zinc-100 transition-colors cursor-pointer font-bold">−</button>
                      <input type="number" min={funcionario.nivelAtual + 1} max={50} value={novoNivel}
                        onChange={(e) => setNovoNivel(parseInt(e.target.value, 10) || prog.proximoNivel)}
                        className="flex-1 px-3 py-2.5 text-[13px] text-center font-bold border border-zinc-200 rounded-lg bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300" />
                      <button type="button" onClick={() => setNovoNivel((v) => Math.min(50, v + 1))}
                        className="whitespace-nowrap w-9 h-9 flex items-center justify-center rounded-lg border border-zinc-200 text-zinc-600 hover:bg-zinc-100 transition-colors cursor-pointer font-bold">+</button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-1.5">Novo Vencimento (€)</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-zinc-400 font-medium">€</span>
                      <input type="number" min={0} step={0.01} value={novoVencimento}
                        onChange={(e) => setNovoVencimento(parseFloat(e.target.value) || 0)}
                        className="w-full pl-7 pr-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300" />
                    </div>
                    {novoVencimento > 0 && novoVencimento !== funcionario.vencimento && (
                      <p className="text-[11px] text-zinc-400 mt-1">
                        Anterior: {(funcionario.vencimento ?? 0).toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' })}
                        {' '}→ Novo: {novoVencimento.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' })}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-1.5">Observações</label>
                    <textarea value={observacoes} onChange={(e) => setObservacoes(e.target.value)}
                      rows={2} maxLength={500} placeholder="Motivo ou notas sobre a progressão..."
                      className="w-full px-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300 resize-none" />
                    <div className="flex justify-end mt-1">
                      <span className={`text-[11px] ${observacoes.length > 450 ? 'text-amber-500' : 'text-zinc-400'}`}>{observacoes.length}/500</span>
                    </div>
                  </div>

                  {/* Acelerador Remuneratório */}
                  <div
                    onClick={() => { if (!jaUsouAcelerador) setAceleradorRemuneratorio((v) => !v); }}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all select-none ${
                      jaUsouAcelerador
                        ? 'border-zinc-100 bg-zinc-50 cursor-not-allowed opacity-60'
                        : aceleradorRemuneratorio
                          ? 'border-amber-400 bg-amber-50 cursor-pointer'
                          : 'border-zinc-200 bg-zinc-50 hover:border-zinc-300 cursor-pointer'
                    }`}
                  >
                    <div className={`w-5 h-5 flex items-center justify-center rounded flex-shrink-0 border-2 transition-all ${
                      jaUsouAcelerador
                        ? 'bg-zinc-200 border-zinc-200'
                        : aceleradorRemuneratorio ? 'bg-amber-500 border-amber-500' : 'bg-white border-zinc-300'
                    }`}>
                      {aceleradorRemuneratorio && !jaUsouAcelerador && <i className="ri-check-line text-white text-xs" />}
                      {jaUsouAcelerador && <i className="ri-lock-line text-zinc-400 text-xs" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-[13px] font-semibold leading-tight ${
                        jaUsouAcelerador ? 'text-zinc-400' : aceleradorRemuneratorio ? 'text-amber-800' : 'text-zinc-700'
                      }`}>
                        Acelerador Remuneratório
                        {jaUsouAcelerador && <span className="ml-1.5 text-[10px] font-normal text-zinc-400">(já utilizado)</span>}
                      </p>
                      <p className="text-[11px] text-zinc-400 mt-0.5">
                        {jaUsouAcelerador
                          ? 'Este funcionário já usufruiu do acelerador numa progressão anterior'
                          : aceleradorRemuneratorio
                            ? `Limiar reduzido para 6 pts (era ${limiarUsado} pts)`
                            : 'Marcar para reduzir o limiar para 6 pts'}
                      </p>
                    </div>
                    {!jaUsouAcelerador && aceleradorRemuneratorio && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full flex-shrink-0">
                        <i className="ri-flashlight-line text-[10px]" />
                        6 pts
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 text-[12px] text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2.5">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className="ri-error-warning-line text-sm" /></div>
                  {error}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-zinc-100 flex items-center justify-end gap-2.5 flex-shrink-0 bg-zinc-50/40">
              <button onClick={handleClose}
                className="whitespace-nowrap px-4 py-2 text-[13px] font-medium text-zinc-600 border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors cursor-pointer">
                Cancelar
              </button>
              <button onClick={handleConfirmarAutomatica} disabled={saving}
                className="whitespace-nowrap flex items-center gap-2 px-5 py-2 text-[13px] font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors cursor-pointer disabled:opacity-60">
                {saving
                  ? <><i className="ri-loader-4-line animate-spin text-sm" />A guardar...</>
                  : <><i className="ri-check-line text-sm" />Confirmar Progressão</>
                }
              </button>
            </div>
          </>
        )}

        {/* ══════════════════════════════════════════
            TAB: MANUAL (livre, sem limiar)
        ══════════════════════════════════════════ */}
        {activeTab === 'manual' && (
          <>
            <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">

              {/* Aviso */}
              <div className="flex items-start gap-3 bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-3.5">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 text-zinc-400">
                  <i className="ri-information-line text-sm" />
                </div>
                <p className="text-[12px] text-zinc-600 leading-relaxed">
                  Regista uma progressão <strong>sem necessidade de atingir o limiar de pontos</strong>. Define livremente os pontos a descontar e o novo nível.
                </p>
              </div>

              {/* Bloco visual de transição */}
              <div>
                <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-3">Transição de Nível</p>
                <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
                  <div className="flex items-center justify-center gap-6 mb-4">
                    <div className="text-center">
                      <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-white border border-emerald-200 mx-auto mb-1.5">
                        <span className="text-[22px] font-bold text-zinc-700">{funcionario.nivelAtual}</span>
                      </div>
                      <p className="text-[11px] text-zinc-500">Nível atual</p>
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-emerald-500 text-white">
                        <i className="ri-arrow-right-line text-sm" />
                      </div>
                      <span className="text-[10px] text-emerald-600 font-semibold">+{Math.max(0, pmNovoNivel - funcionario.nivelAtual)}</span>
                    </div>
                    <div className="text-center">
                      <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-emerald-500 text-white mx-auto mb-1.5">
                        <span className="text-[22px] font-bold">{pmNovoNivel}</span>
                      </div>
                      <p className="text-[11px] text-emerald-700 font-medium">Novo nível</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 pt-3 border-t border-emerald-200/60">
                    <div className="text-center">
                      <p className="text-[11px] text-zinc-500 mb-0.5">Pontos atuais</p>
                      <p className="text-[14px] font-bold text-zinc-700">{pontosCalculados}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[11px] text-zinc-500 mb-0.5">Desconto</p>
                      <p className="text-[14px] font-bold text-rose-600">−{pmPontosNum}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[11px] text-zinc-500 mb-0.5">Ficam</p>
                      <p className="text-[14px] font-bold text-emerald-700">{pmPontosApos}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Campos */}
              <div className="space-y-4">
                {/* Pontos a descontar */}
                <div>
                  <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">Pontos a descontar *</label>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setPmPontosDescontar((v) => String(Math.max(0, (parseFloat(v) || 0) - 0.5)))}
                      className="whitespace-nowrap w-9 h-9 flex items-center justify-center rounded-lg border border-zinc-200 text-zinc-600 hover:bg-zinc-100 transition-colors cursor-pointer font-bold flex-shrink-0">−</button>
                    <input type="number" min={0} step={0.5} value={pmPontosDescontar}
                      onChange={(e) => setPmPontosDescontar(e.target.value)}
                      className="flex-1 px-3 py-2.5 text-[15px] font-bold text-center border border-zinc-200 rounded-lg bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300" />
                    <button onClick={() => setPmPontosDescontar((v) => String((parseFloat(v) || 0) + 0.5))}
                      className="whitespace-nowrap w-9 h-9 flex items-center justify-center rounded-lg border border-zinc-200 text-zinc-600 hover:bg-zinc-100 transition-colors cursor-pointer font-bold flex-shrink-0">+</button>
                  </div>
                  <div className="flex gap-1.5 mt-2 flex-wrap">
                    {[limiarNormalManual, limiarNormalManual * 2, 6, 8, 10]
                      .filter((v, i, a) => a.indexOf(v) === i && v > 0)
                      .map((v) => (
                        <button key={v} onClick={() => setPmPontosDescontar(String(v))}
                          className={`whitespace-nowrap px-2.5 py-1 text-[12px] rounded-lg border transition-colors cursor-pointer ${
                            parseFloat(pmPontosDescontar) === v ? 'bg-zinc-900 text-white border-zinc-900' : 'border-zinc-200 text-zinc-500 hover:border-zinc-400'
                          }`}>
                          {v} pts{v === limiarNormalManual ? ' (limiar)' : v === 6 ? ' (acelerador)' : ''}
                        </button>
                      ))}
                  </div>
                </div>

                {/* Novo nível */}
                <div>
                  <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">Novo Nível *</label>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setPmNovoNivel((v) => Math.max(funcionario.nivelAtual + 1, v - 1))}
                      className="whitespace-nowrap w-9 h-9 flex items-center justify-center rounded-lg border border-zinc-200 text-zinc-600 hover:bg-zinc-100 transition-colors cursor-pointer font-bold flex-shrink-0">−</button>
                    <input type="number" min={funcionario.nivelAtual + 1} max={50} value={pmNovoNivel}
                      onChange={(e) => setPmNovoNivel(parseInt(e.target.value, 10) || funcionario.nivelAtual + 1)}
                      className="flex-1 px-3 py-2.5 text-[15px] font-bold text-center border border-zinc-200 rounded-lg bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300" />
                    <button onClick={() => setPmNovoNivel((v) => Math.min(50, v + 1))}
                      className="whitespace-nowrap w-9 h-9 flex items-center justify-center rounded-lg border border-zinc-200 text-zinc-600 hover:bg-zinc-100 transition-colors cursor-pointer font-bold flex-shrink-0">+</button>
                  </div>
                </div>

                {/* Data */}
                <div>
                  <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">Data da Progressão *</label>
                  <input type="date" value={pmData} onChange={(e) => setPmData(e.target.value)}
                    className="w-full px-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300" />
                </div>

                {/* Novo vencimento */}
                <div>
                  <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">Novo Vencimento (€)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-zinc-400 font-medium">€</span>
                    <input type="number" min={0} step={0.01} value={pmNovoVencimento}
                      onChange={(e) => setPmNovoVencimento(parseFloat(e.target.value) || 0)}
                      className="w-full pl-7 pr-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300" />
                  </div>
                  {pmNovoVencimento > 0 && pmNovoVencimento !== funcionario.vencimento && (
                    <p className="text-[11px] text-zinc-400 mt-1">
                      Anterior: {(funcionario.vencimento ?? 0).toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' })}
                      {' '}→ Novo: {pmNovoVencimento.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' })}
                    </p>
                  )}
                </div>

                {/* Observações */}
                <div>
                  <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">Observações</label>
                  <textarea value={pmObservacoes} onChange={(e) => setPmObservacoes(e.target.value)}
                    rows={2} maxLength={500} placeholder="Motivo ou notas sobre esta progressão manual..."
                    className="w-full px-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300 resize-none" />
                  <div className="flex justify-end mt-1">
                    <span className={`text-[11px] ${pmObservacoes.length > 450 ? 'text-amber-500' : 'text-zinc-400'}`}>{pmObservacoes.length}/500</span>
                  </div>
                </div>

                {/* Acelerador Remuneratório */}
                <div
                  onClick={() => { if (!jaUsouAcelerador) setPmAceleradorRemuneratorio((v) => !v); }}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all select-none ${
                    jaUsouAcelerador
                      ? 'border-zinc-100 bg-zinc-50 cursor-not-allowed opacity-60'
                      : pmAceleradorRemuneratorio
                        ? 'border-amber-400 bg-amber-50 cursor-pointer'
                        : 'border-zinc-200 bg-zinc-50 hover:border-zinc-300 cursor-pointer'
                  }`}
                >
                  <div className={`w-5 h-5 flex items-center justify-center rounded flex-shrink-0 border-2 transition-all ${
                    jaUsouAcelerador
                      ? 'bg-zinc-200 border-zinc-200'
                      : pmAceleradorRemuneratorio ? 'bg-amber-500 border-amber-500' : 'bg-white border-zinc-300'
                  }`}>
                    {pmAceleradorRemuneratorio && !jaUsouAcelerador && <i className="ri-check-line text-white text-xs" />}
                    {jaUsouAcelerador && <i className="ri-lock-line text-zinc-400 text-xs" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-[13px] font-semibold leading-tight ${
                      jaUsouAcelerador ? 'text-zinc-400' : pmAceleradorRemuneratorio ? 'text-amber-800' : 'text-zinc-700'
                    }`}>
                      Acelerador Remuneratório
                      {jaUsouAcelerador && <span className="ml-1.5 text-[10px] font-normal text-zinc-400">(já utilizado)</span>}
                    </p>
                    <p className="text-[11px] text-zinc-400 mt-0.5">
                      {jaUsouAcelerador
                        ? 'Este funcionário já usufruiu do acelerador numa progressão anterior'
                        : pmAceleradorRemuneratorio
                          ? `Pontos a descontar ajustados para 6 pts (era ${limiarNormalManual} pts)`
                          : 'Marcar para ajustar o desconto para 6 pts'}
                    </p>
                  </div>
                  {!jaUsouAcelerador && pmAceleradorRemuneratorio && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full flex-shrink-0">
                      <i className="ri-flashlight-line text-[10px]" />
                      6 pts
                    </span>
                  )}
                </div>
              </div>

              {pmError && (
                <div className="flex items-center gap-2 text-[13px] text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2.5">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className="ri-error-warning-line text-sm" /></div>
                  {pmError}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-zinc-100 flex items-center justify-end gap-2.5 flex-shrink-0 bg-zinc-50/40">
              <button onClick={handleClose} disabled={pmSaving}
                className="whitespace-nowrap px-4 py-2 text-[13px] font-medium text-zinc-600 border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors cursor-pointer disabled:opacity-50">
                Cancelar
              </button>
              <button onClick={handleConfirmarManual} disabled={pmSaving}
                className="whitespace-nowrap flex items-center gap-2 px-5 py-2 text-[13px] font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors cursor-pointer disabled:opacity-60">
                {pmSaving
                  ? <><i className="ri-loader-4-line animate-spin text-sm" />A registar...</>
                  : <><i className="ri-arrow-up-circle-line text-sm" />Registar Progressão</>
                }
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}